import { create } from "zustand";
import { devtools } from "zustand/middleware";

import type { IndustryNodeId } from "app/pages/sales-intelligence/domain/industry/types/taxonomy";

interface IIndustrySuggestionsState {
    /** Session-wide: a node dismissed on one card stays dismissed on every other. */
    readonly dismissedIds: readonly IndustryNodeId[];
    readonly mountedCards: number;
    readonly claimedImpressionKeys: readonly string[];
    readonly dismiss: (id: IndustryNodeId) => void;
    readonly resetDismissals: () => void;
    readonly registerCard: () => void;
    /** The last instance unmounting releases the claim, so the next page view reports its own impression. */
    readonly unregisterCard: () => void;
    /** True only for the first caller per key, so a card mounted twice reports one impression. */
    readonly claimImpression: (key: string) => boolean;
    readonly releaseImpression: () => void;
}

/**
 * Session-only suggestion state, never persisted with the filter value. A store rather than view state
 * because the filter panel mounts the same card twice (collapsed list and category overlay), and both
 * must agree on what was dismissed and whether the row was already reported.
 */
export const useIndustrySuggestionsStore = create<IIndustrySuggestionsState>()(
    devtools(
        (set, get) => ({
            dismissedIds: [],
            mountedCards: 0,
            claimedImpressionKeys: [],
            dismiss: (id) => {
                if (get().dismissedIds.includes(id)) {
                    return;
                }

                set((state) => ({ dismissedIds: [...state.dismissedIds, id] }), false, "dismiss");
            },
            resetDismissals: () => set({ dismissedIds: [] }, false, "resetDismissals"),
            registerCard: () => set((state) => ({ mountedCards: state.mountedCards + 1 }), false, "registerCard"),
            unregisterCard: () => {
                const mountedCards = Math.max(get().mountedCards - 1, 0);

                set(
                    mountedCards === 0 ? { mountedCards, claimedImpressionKeys: [] } : { mountedCards },
                    false,
                    "unregisterCard",
                );
            },
            claimImpression: (key) => {
                if (get().claimedImpressionKeys.includes(key)) {
                    return false;
                }

                set(
                    (state) => ({ claimedImpressionKeys: [...state.claimedImpressionKeys, key] }),
                    false,
                    "claimImpression",
                );

                return true;
            },
            releaseImpression: () => set({ claimedImpressionKeys: [] }, false, "releaseImpression"),
        }),
        { name: "IndustrySuggestionsStore" },
    ),
);
